import React, { Fragment, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import tw from 'twin.macro'
import { Form, Formik } from 'formik'
import { Typography } from '@mui/material'
import { useSelector } from 'react-redux'
import { Button } from 'src/components/shared/Button'
import { CommonModal } from 'src/components/shared/CommonModal'
import { FormikCheckbox } from 'src/components/shared/form/formik/FormikCheckbox'
import { FormikTextField } from 'src/components/shared/form/formik/FormikTextField'
import { AdminBulkApprovePayments } from 'src/constants/actionTypes'
import { useAsyncDispatchWithErrorToast } from 'src/hooks/useAsyncDispatchWithErrorToast'
import type { BulkApproveResult, PendingPayment } from 'src/interfaces/paymentsReview'
import { getBulkApproveProgress, getPaymentReviewSubmitting } from 'src/selectors/paymentsReview'
import { addS } from 'src/utils/baseUtils'
import { formatCentsAsUsd } from 'src/utils/invoiceMoney'
import { summarizePayments } from 'src/utils/paymentsOps/pendingPayments'
import { BulkApproveResults } from './BulkApproveResults'
import { bulkApproveValidator } from './BulkApproveModal.validator'

interface Props {
  isOpen: boolean
  /**
   * The batch to approve — a snapshot the parent took when the user opened the modal, so it
   * does not shrink under the results view when the queue refetches after the approval.
   */
  payments: PendingPayment[]
  onClose(): void
}

interface BulkApproveFormValues {
  reviewNotes: string
  isAcknowledged: boolean
}

const FORM_ID = 'bulk-approve-form'

const INITIAL_VALUES: BulkApproveFormValues = { reviewNotes: '', isAcknowledged: false }

const BulkApproveModalComponent = ({ isOpen, payments, onClose }: Props) => {
  const { asyncDispatchWithErrorToast } = useAsyncDispatchWithErrorToast()
  const isSubmitting = useSelector(getPaymentReviewSubmitting)
  const progress = useSelector(getBulkApproveProgress)

  const [result, setResult] = useState<BulkApproveResult | null>(null)

  // Lets an in-flight submit see that the modal was closed before its response arrived.
  const isOpenRef = useRef<boolean>(isOpen)

  useEffect(() => {
    isOpenRef.current = isOpen
  }, [isOpen])

  const summary = useMemo(() => {
    return summarizePayments(payments)
  }, [payments])

  // The modal stays mounted while closed, so a finished batch's results are dropped on close.
  // Backdrop click and Escape also land here: ignore them mid-request so the approval's outcome
  // is never lost behind a closed modal.
  const handleClose = useCallback(() => {
    if (isSubmitting) {
      return
    }

    setResult(null)
    onClose()
  }, [isSubmitting, onClose])

  const handleSubmit = useCallback(
    async (values: BulkApproveFormValues) => {
      const earningIds = payments.map((payment) => {
        return payment.id
      })

      const bulkResult = await asyncDispatchWithErrorToast<BulkApproveResult>(
        AdminBulkApprovePayments.request({ earningIds, reviewNotes: values.reviewNotes.trim() }),
      )

      // A result for a batch the user already left must not outlive the modal.
      if (bulkResult && isOpenRef.current) {
        setResult(bulkResult)
      }
    },
    [asyncDispatchWithErrorToast, payments],
  )

  if (!isOpen) {
    return null
  }

  const total = formatCentsAsUsd(summary.totalCents)
  const confirmLabel = progress
    ? `Approving ${progress.done} of ${progress.total}…`
    : `Approve ${summary.count} · ${total}`

  if (result) {
    return (
      <CommonModal
        isOpen
        title="Batch complete"
        className={tw`max-w-[560px]`}
        onClose={handleClose}
        buttons={
          <Button size="small" color="dark" onClick={handleClose}>
            Close
          </Button>
        }
      >
        <BulkApproveResults result={result} payments={payments} />
      </CommonModal>
    )
  }

  return (
    <Formik<BulkApproveFormValues>
      initialValues={INITIAL_VALUES}
      validationSchema={bulkApproveValidator}
      validateOnMount
      onSubmit={handleSubmit}
    >
      {({ isValid }) => {
        return (
          <CommonModal
            isOpen
            title="Approve all visible payments?"
            className={tw`max-w-[560px]`}
            onClose={handleClose}
            buttons={
              <Fragment>
                <Button size="small" color="grey" disabled={isSubmitting} onClick={handleClose}>
                  Cancel
                </Button>
                <Button
                  size="small"
                  color="dark"
                  type="submit"
                  form={FORM_ID}
                  loading={isSubmitting}
                  disabled={!isValid || isSubmitting}
                  data-testid="bulk-approve-confirm"
                >
                  {confirmLabel}
                </Button>
              </Fragment>
            }
          >
            <Form id={FORM_ID} css={tw`flex flex-col gap-4`}>
              <Typography variant="body2">
                Approve all {summary.count} pending {addS(summary.count, 'payment')} currently shown
                — {total} across {summary.partnerCount} {addS(summary.partnerCount, 'partner')}?
              </Typography>
              <FormikTextField<BulkApproveFormValues>
                name="reviewNotes"
                label="Reason (applied to every payment) *"
                placeholder="Why are these being approved?"
                data-testid="bulk-approve-reason"
              />
              <FormikCheckbox<BulkApproveFormValues>
                name="isAcknowledged"
                label="I understand this approves every payment shown and cannot be undone."
                inputProps={{ 'data-testid': 'bulk-approve-acknowledgement' }}
              />
              {progress ? (
                <Typography
                  variant="body2"
                  css={tw`text-xs text-shades-500`}
                  data-testid="bulk-approve-progress"
                >
                  {`${progress.done} of ${progress.total} processed — keep this window open until the batch finishes.`}
                </Typography>
              ) : null}
            </Form>
          </CommonModal>
        )
      }}
    </Formik>
  )
}

export const BulkApproveModal = memo(BulkApproveModalComponent)
